import * as React from 'react';
import { useCallback, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '../../primitives/Icon/icons';
import { Icon } from '../../primitives/Icon/Icon';
import { useDialog } from '../../../utils/dialog';
import {
  backdrop as backdropCls,
  centered as centeredCls,
  dialog as dialogCls,
  header as headerCls,
  title as titleCls,
  close as closeCls,
  body as bodyCls,
  footer as footerCls,
  dragHandle as dragHandleCls,
} from './Modal.css';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Controls visibility. */
  open: boolean;
  /** Fired on backdrop click (when dismissible), Escape, or close ✕ click. */
  onClose: () => void;
  /** Bold heading rendered in the dialog header. */
  title?: string;
  size?: ModalSize;
  /** Click backdrop or press Escape to close. Default true. */
  dismissible?: boolean;
  /**
   * Full-bleed body — drops the default 24px body padding and the body's
   * own scroll, instead stretching the body to fill the dialog (flex,
   * `min-height:0`) and handing scroll ownership to the child. Use for a
   * full-bleed flex surface (e.g. a fluid `ChatPanel`) that should reach
   * the dialog's rounded border on all four sides and manage its own
   * internal scroll region. Default false (padded, self-scrolling body).
   */
  flushBody?: boolean;
  /** Rendered in the footer row, aligned to the right. */
  actions?: React.ReactNode;
  /** Label for screen-readers when `title` is omitted. */
  'aria-label'?: string;
  /** Custom class merged onto the dialog surface (NOT the backdrop). */
  className?: string;
  /** ref-as-prop pointing at the dialog surface element. */
  ref?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}

/**
 * Modal — portal-rendered centred dialog. White card on a 35%-black
 * backdrop, with a draggable handle, optional title row + close ✕,
 * scrolling body, and optional footer for actions.
 *
 * Behaviour:
 *   - `dismissible` (default true) — backdrop click + Escape both close,
 *     and a close ✕ renders in the header (with or without a `title`).
 *   - Focus is trapped inside the dialog while open and restored to the
 *     trigger on close (via `useDialog`).
 *   - When `title` is supplied, the dialog announces it via
 *     `aria-labelledby`; otherwise pass `aria-label` for SR identity.
 *   - Extra `HTMLAttributes` (`data-testid`, `id`, `aria-describedby`,
 *     …) spread onto the dialog surface.
 *
 * Prototype: `http://localhost:8000/` "Send to Client" / "Trust Confirm"
 * modals. 600×auto, 12px radius, `0 16px 48px rgba(0,0,0,0.12)` shadow.
 */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  dismissible = true,
  flushBody = false,
  actions,
  'aria-label': ariaLabel,
  className,
  ref,
  children,
  ...rest
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useDialog({ open, containerRef: dialogRef, onClose, dismissible });

  if (!open || typeof document === 'undefined') return null;

  // The header row renders whenever there's a title OR a dismissible close
  // affordance — so a titleless dismissible Modal still has a visible
  // close ✕ (L3). The close button sits at the right regardless; with no
  // title the row is otherwise empty (the ✕ floats right via the header's
  // `justify-content: space-between`).
  const showHeader = Boolean(title) || dismissible;

  return createPortal(
    <>
      <div className={backdropCls} onClick={close} aria-hidden="true" />
      <div className={centeredCls}>
        <div
          ref={(node) => {
            dialogRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={!title ? ariaLabel : undefined}
          tabIndex={-1}
          className={[dialogCls({ size }), className].filter(Boolean).join(' ')}
          {...rest}
        >
          <div className={dragHandleCls} aria-hidden="true" />
          {showHeader && (
            <header className={headerCls}>
              {title && (
                <h2 id={titleId} className={titleCls}>
                  {title}
                </h2>
              )}
              {dismissible && (
                <button type="button" aria-label="Close" onClick={onClose} className={closeCls}>
                  <Icon as={X} size={18} strokeWidth={1.75} />
                </button>
              )}
            </header>
          )}
          <div className={bodyCls({ flush: flushBody })}>{children}</div>
          {actions && <footer className={footerCls}>{actions}</footer>}
        </div>
      </div>
    </>,
    document.body,
  );
}
Modal.displayName = 'Modal';
